import * as Communicator from "@ts3d-hoops/web-viewer";
import {
    ColorSetting,
    DisplayUnits,
    EffectSetting,
    GeneralSettingState,
    PointCloudSetting,
    SettingState,
} from "common/define";
import { GlobalState } from "common/global";
import { SystemConstants } from "common/system-constants";
import CZoomOperator from "container/viewer3d/extends/CZoomOperator";
import { JsonSetting } from "container/viewer3d/json-setting/jsonSetting";
import ModelHelper from "container/viewer3d/model/model.helper";
import AreaSelectOperator from "container/viewer3d/operator/areaSelect.operator";
import OrbitOperator from "container/viewer3d/operator/orbit.operator";
import SelectOperator from "container/viewer3d/operator/select.operator";
import { RGBColor } from "react-color";
import Utils from "utils/utils";

export const defaultGeneralSetting: GeneralSettingState = {
    axis: true,
    cube: true,
    // HOOPS bản mới đảo số Perspective và Orthographic với nhau nên đổi default projection thành Perspective
    projectionModelDefault: 1,
    xRayOpacity: 15,
    ghostingOpacity: 5,
    backGroundColor: { r: 255, g: 255, b: 255, a: 100 },
    speedZoom: 0,
    bimOrbitEnabled: true,
    sheetBackGroundColor: { r: 155, g: 155, b: 155, a: 100 },
    sheetColor: { r: 0, g: 0, b: 0, a: 100 },
    sheetShadow: { r: 74, g: 74, b: 74, a: 100 },
    gradient: false,
    gradientColor: { r: 255, g: 255, b: 255, a: 100 },
    showLineWeight: false,
    displayUnit: DisplayUnits.Milimeter,
    precision: 2,
};
export const defaultEffectSetting: EffectSetting = {
    ambientOcclusion: false,
    ambientOcclusionRadius: 0.03,
    antiAliasing: true,
    bloom: false,
    bloomIntensityScale: 0.01,
    threshold: 40,
    silhoutteEdges: false,
    reflectionPlane: false,
    shadows: false,
    interaction: false,
    blurSample: 40,
};
export const defaultColorSetting: ColorSetting = {
    capingGeometryFace: { r: 100, g: 100, b: 100, a: 100 },
    capingGeometryLine: { r: 150, g: 150, b: 150, a: 100 },
    capingGeometryPlane: { r: 200, g: 200, b: 200, a: 100 },
    selectionMeasurement: { r: 250, g: 250, b: 250, a: 100 },
    enablePMIOverride: false,
    selectionPMI: { r: 50, g: 50, b: 50, a: 100 },
};
export const defaultPointCloudSetting: PointCloudSetting = {
    splatSize: 0.03,
    enableSplat: true,
    enableEyeLighting: 0.03,
    cloudSplatMode: 1,
    enableLod: false,
    enableAutoLod: true,
    lodNearDistance: 8,
    lodFarDistance: 24,
    lodOverrideLevel: -1,
    lodDwellMs: 300,
    showLodInfo: false,
};

export const defaultSetting: SettingState = {
    general: defaultGeneralSetting,
    effect: defaultEffectSetting,
    color: defaultColorSetting,
    pointCloud: defaultPointCloudSetting,
    fetchSettingFail: false,
    customSetting: false,
};

export default class SettingHelper {
    static isViewerCanvasReady = (
        webViewer: Communicator.IWebViewer
    ): boolean => {
        try {
            const canvas = webViewer.view.getCanvasSize();
            if (!canvas || canvas.x <= 0 || canvas.y <= 0) {
                return false;
            }
            const container = webViewer.getViewElement();
            if (!container) {
                return false;
            }
            return container.clientWidth > 0 && container.clientHeight > 0;
        } catch (_error) {
            return false;
        }
    };

    static normalizePointCloudSetting = (
        settings?: Partial<PointCloudSetting> | null
    ): PointCloudSetting => {
        return {
            ...defaultPointCloudSetting,
            ...(settings ?? {}),
        };
    };

    static getPointCloudLodLevel = (
        settings: PointCloudSetting,
        distanceToTarget: number,
        modelDiagonal?: number | null,
        currentLevel?: number | null
    ): number | null => {
        const normalized = SettingHelper.normalizePointCloudSetting(settings);
        if (!normalized.enableLod) {
            return null;
        }
        if (normalized.lodOverrideLevel >= 0) {
            return Math.max(0, Math.min(2, normalized.lodOverrideLevel));
        }
        if (!normalized.enableAutoLod) {
            return null;
        }

        const { near, far } = SettingHelper.getScaleAwareLodRange(
            normalized,
            modelDiagonal
        );
        const hysteresisDistance = Math.max(0.1, (far - near) * 0.1);
        const current =
            typeof currentLevel === "number"
                ? Math.max(0, Math.min(2, currentLevel))
                : null;

        if (current === null) {
            if (distanceToTarget <= near) return 2;
            if (distanceToTarget >= far) return 0;
            return 1;
        }

        if (current === 2) {
            if (distanceToTarget > near + hysteresisDistance) {
                return distanceToTarget >= far + hysteresisDistance ? 0 : 1;
            }
            return 2;
        }

        if (current === 0) {
            if (distanceToTarget < far - hysteresisDistance) {
                return distanceToTarget <= near - hysteresisDistance ? 2 : 1;
            }
            return 0;
        }

        if (distanceToTarget <= near - hysteresisDistance) return 2;
        if (distanceToTarget >= far + hysteresisDistance) return 0;
        return 1;
    };

    static getLodAdjustedSplatSize = (
        baseSplatSize: number,
        lodLevel: number | null
    ): number => {
        if (lodLevel === null) return baseSplatSize;
        if (lodLevel === 2) return baseSplatSize;
        if (lodLevel === 1) return baseSplatSize * 0.82;
        return baseSplatSize * 0.62;
    };

    static getAdaptiveSplatSizeByDistance = (
        settings: PointCloudSetting,
        distanceToTarget: number,
        modelDiagonal?: number | null
    ): number => {
        const normalized = SettingHelper.normalizePointCloudSetting(settings);

        if (!normalized.enableLod) {
            return normalized.splatSize;
        }

        if (!normalized.enableAutoLod) {
            if (normalized.lodOverrideLevel >= 0) {
                const forcedLevel = Math.max(
                    0,
                    Math.min(2, normalized.lodOverrideLevel)
                );
                return SettingHelper.getLodAdjustedSplatSize(
                    normalized.splatSize,
                    forcedLevel
                );
            }
            return normalized.splatSize;
        }

        const { near, far } = SettingHelper.getScaleAwareLodRange(
            normalized,
            modelDiagonal
        );
        const distance = Math.max(distanceToTarget, near);
        const factorAtFar = 0.9;
        const minFactor = 0.62;

        if (distance <= far) {
            const t = (distance - near) / (far - near);
            const factor = 1 - (1 - factorAtFar) * t;
            return normalized.splatSize * Math.max(minFactor, factor);
        }

        const overflowRatio = (distance - far) / far;
        const factorBeyondFar =
            factorAtFar / (1 + 0.8 * Math.log10(1 + overflowRatio * 9));
        return normalized.splatSize * Math.max(minFactor, factorBeyondFar);
    };

    static getScaleAwareLodRange = (
        settings: PointCloudSetting,
        modelDiagonal?: number | null
    ): { near: number; far: number } => {
        const diagonal = Math.max(0, modelDiagonal ?? 0);
        if (diagonal <= 0) {
            // Fallback to fixed values when no diagonal available
            return { near: 1.0, far: 10.0 };
        }

        // Simplified: only use model diagonal for LOD thresholds
        const near = diagonal * 0.01;
        const far = diagonal * 0.12;

        return { near, far };
    };

    static getDefaultSheetColor = (extension: string): GeneralSettingState => {
        let defaultSheetColor: RGBColor = { r: 255, g: 255, b: 255, a: 100 };
        switch (extension) {
            case "dwg":
            case "dgn":
                defaultSheetColor = { r: 0, g: 0, b: 0, a: 100 };
                break;
            default:
                break;
        }
        const cloneGeneralSetting: GeneralSettingState = {
            ...defaultGeneralSetting,
            sheetColor: defaultSheetColor,
        };
        return cloneGeneralSetting;
    };
    static setBackgroundColor(
        webViewer: Communicator.IWebViewer,
        settingsGradient: boolean,
        bg: RGBColor,
        gradient: RGBColor,
        viewId?: string,
        show?: boolean
    ): void {
        const bgColor = new Communicator.Color(bg.r, bg.g, bg.b);
        const gradientColor = new Communicator.Color(
            gradient.r,
            gradient.g,
            gradient.b
        );
        let bottomColor: Communicator.Color = bgColor;
        settingsGradient && (bottomColor = gradientColor);
        webViewer.view.setBackgroundColor(bgColor, bottomColor);
        if (webViewer.view.getDrawMode() === Communicator.DrawMode.HiddenLine) {
            ModelHelper.configHiddenLineMode(webViewer, bgColor, bottomColor);
        }
        if (webViewer && viewId && show) {
            const viewer = GlobalState.mapActiveView.get(viewId);
            if (viewer) {
                const jsonData = GlobalState.mapJsonSetting.get(viewId);
                JsonSetting.prototype.changeColorBlackWhite(viewer, jsonData);
            }
        }
    }
    static setGeneralSetting = (
        webViewer: Communicator.IWebViewer,
        settings: GeneralSettingState,
        viewId?: string
    ): void => {
        const axis = webViewer.view.getAxisTriad();
        const cube = webViewer.view.getNavCube();
        const { xRayOpacity, ghostingOpacity, gradient } = settings;
        const isDrawing = webViewer.sheetManager.isDrawingSheetActive();
        if (!isDrawing && webViewer.overlayManager) {
            try {
                if (settings.axis) {
                    axis.enable();
                } else {
                    axis.disable();
                }
                if (settings.cube) {
                    cube.enable();
                } else {
                    cube.disable();
                }
                // webViewer.view.setProjectionMode(projectionModelDefault);
            } catch (e) {
                console.warn(e);
            }
        } else {
            const defaultBG = webViewer.sheetManager.getSheetBackgroundColor();
            const defaultShadow = webViewer.sheetManager.getSheetShadowColor();
            const defaultColor = webViewer.sheetManager.getSheetColor();
            const sheetShadowColor = settings.sheetShadow
                ? new Communicator.Color(
                      settings.sheetShadow.r,
                      settings.sheetShadow.g,
                      settings.sheetShadow.b
                  )
                : defaultShadow;
            const sheetColor = settings.sheetColor
                ? new Communicator.Color(
                      settings.sheetColor.r,
                      settings.sheetColor.g,
                      settings.sheetColor.b
                  )
                : defaultColor;
            const sheetBackGroundColor = settings.sheetBackGroundColor
                ? new Communicator.Color(
                      settings.sheetBackGroundColor.r,
                      settings.sheetBackGroundColor.g,
                      settings.sheetBackGroundColor.b
                  )
                : defaultBG;
            webViewer.sheetManager.setSheetColors(
                sheetBackGroundColor,
                sheetColor,
                sheetShadowColor
            );
            // webViewer.view.setProjectionMode(Communicator.Projection.Orthographic);
        }
        SettingHelper.setBackgroundColor(
            webViewer,
            gradient,
            settings.backGroundColor,
            settings.gradientColor,
            viewId,
            settings.showLineWeight
        );
        ModelHelper.applyXrayOpacity(webViewer, xRayOpacity, ghostingOpacity);
        SettingHelper.setZoomSpeed(webViewer, settings.speedZoom);
        SettingHelper.setBimOrbitEnabled(viewId, settings.bimOrbitEnabled);
        if (viewId && settings.showLineWeight) {
            const viewer = GlobalState.mapActiveView.get(viewId);
            if (viewer) {
                GlobalState.mapLineWeight.set(viewId, settings.showLineWeight);
                SettingHelper.setLineWeight(
                    viewer,
                    settings.showLineWeight,
                    viewId
                );
            }
        }
    };
    static setLineWeight = (
        webViewer: Communicator.IWebViewer,
        show: boolean,
        viewId?: string
    ): void => {
        if (webViewer && viewId) {
            const jsonData = GlobalState.mapJsonSetting.get(viewId);
            if (jsonData)
                JsonSetting.prototype.callApplyLineWeight(
                    webViewer,
                    jsonData,
                    show
                );
        }
    };

    static setEffectSetting = (
        webViewer: Communicator.IWebViewer,
        settings: EffectSetting
    ): void => {
        const isDrawing = webViewer.sheetManager.isDrawingSheetActive();
        if (isDrawing) {
            webViewer.view.setAmbientOcclusionEnabled(false);
        } else {
            webViewer.view.setAmbientOcclusionEnabled(
                settings.ambientOcclusion
            );
            webViewer.view.setAmbientOcclusionRadius(
                settings.ambientOcclusionRadius > 1
                    ? 1
                    : settings.ambientOcclusionRadius
            );
        }
        webViewer.view.setAntiAliasingMode(settings.antiAliasing ? 0 : 1);
        webViewer.view.setBloomEnabled(settings.bloom);
        webViewer.view.setBloomIntensityScale(
            settings.bloomIntensityScale * Utils.BloomRatioCoefficient
        );
        webViewer.view.setBloomThreshold(settings.threshold / 100.0);
        webViewer.view.setSilhouetteEnabled(settings.silhoutteEdges);
        webViewer.view.setSimpleReflectionEnabled(settings.reflectionPlane);
        webViewer.view.setSimpleShadowEnabled(settings.shadows);
        webViewer.view.setInteractiveDrawLimitIncreaseEnabled(
            settings.interaction
        );
        webViewer.view.setSimpleShadowBlurSamples(settings.blurSample);
    };

    static setColorSetting = (
        webViewer: Communicator.IWebViewer,
        settings: ColorSetting
    ): void => {
        const cappingFaceColor = new Communicator.Color(
            settings.capingGeometryFace.r,
            settings.capingGeometryFace.g,
            settings.capingGeometryFace.b
        );
        const cappingLineColor = new Communicator.Color(
            settings.capingGeometryLine.r,
            settings.capingGeometryLine.g,
            settings.capingGeometryLine.b
        );
        const cappingPlaneColor = new Communicator.Color(
            settings.capingGeometryPlane.r,
            settings.capingGeometryPlane.g,
            settings.capingGeometryPlane.b
        );
        const measureColor = new Communicator.Color(
            settings.selectionMeasurement.r,
            settings.selectionMeasurement.g,
            settings.selectionMeasurement.b
        );
        const pmiColor = new Communicator.Color(
            settings.selectionPMI.r,
            settings.selectionPMI.g,
            settings.selectionPMI.b
        );
        webViewer.model &&
            webViewer.model.setPmiColorOverride(settings.enablePMIOverride);
        webViewer.model && webViewer.model.setPmiColor(pmiColor);
        webViewer.cuttingManager.setCappingFaceColor(cappingFaceColor);
        webViewer.cuttingManager.setCappingLineColor(cappingLineColor);
        webViewer.cuttingManager.setCuttingPlaneColor(cappingPlaneColor);
        webViewer.measureManager.setMeasurementColor(measureColor);
    };

    static setPointCloudSetting = (
        webViewer: Communicator.IWebViewer,
        settings: PointCloudSetting = defaultPointCloudSetting,
        lodLevel: number | null = null,
        adaptiveSplatSize: number | null = null
    ): void => {
        const normalized = SettingHelper.normalizePointCloudSetting(settings);
        const isDrawing = webViewer.sheetManager.isDrawingSheetActive();
        if (normalized.enableEyeLighting > 0) {
            webViewer.view.setEyeDomeLightingEnabled(true);
            webViewer.view.setEyeDomeLightingOpacity(
                normalized.enableEyeLighting
            );
        } else {
            webViewer.view.setEyeDomeLightingEnabled(false);
        }
        const effectiveSplatSize =
            typeof adaptiveSplatSize === "number"
                ? adaptiveSplatSize
                : SettingHelper.getLodAdjustedSplatSize(
                      normalized.splatSize,
                      lodLevel
                  );
        const sizeRaw = SettingHelper.getPointSize(
            effectiveSplatSize,
            normalized.cloudSplatMode,
            webViewer
        );
        const minSize = SettingHelper.getMinimumPointSize(
            normalized.cloudSplatMode,
            webViewer
        );
        const size = Math.max(sizeRaw, minSize);
        if (isDrawing) {
            webViewer.view.setPointSize(
                SystemConstants.POINT_SIZE_DEFAULT,
                Communicator.PointSizeUnit.ScreenPixels
            );
        } else if (normalized.enableSplat) {
            webViewer.view.setPointSize(size, normalized.cloudSplatMode);
        } else {
            webViewer.view.setPointSize(
                SystemConstants.POINT_SIZE_DEFAULT,
                Communicator.PointSizeUnit.ScreenPixels
            );
        }
    };
    static getPointSize = (
        splatSize: number,
        splatMode: Communicator.PointSizeUnit,
        webViewer: Communicator.IWebViewer
    ): number => {
        let pixelSize = 1.0;
        const canvas = webViewer.view.getCanvasSize();
        let canvasSize = Math.min(canvas.x, canvas.y);
        if (canvasSize < 1) canvasSize = 1;

        switch (splatMode) {
            case Communicator.PointSizeUnit.CSSPixels:
            case Communicator.PointSizeUnit.ScreenPixels:
                pixelSize = splatSize * SystemConstants.POINT_SIZE_MAX;
                break;
            case Communicator.PointSizeUnit.ProportionOfBoundingDiagonal:
            case Communicator.PointSizeUnit.ProportionOfScreenWidth:
            case Communicator.PointSizeUnit.ProportionOfScreenHeight:
                pixelSize =
                    (splatSize * SystemConstants.POINT_SIZE_MAX) / canvasSize;
                break;
            case Communicator.PointSizeUnit.World:
                pixelSize =
                    (splatSize * SystemConstants.POINT_SIZE_MAX * 5) /
                    canvasSize;
                break;
            default:
                pixelSize =
                    (splatSize * SystemConstants.POINT_SIZE_MAX) / canvasSize;
        }
        return pixelSize;
    };

    static getMinimumPointSize = (
        splatMode: Communicator.PointSizeUnit,
        webViewer: Communicator.IWebViewer
    ): number => {
        const canvas = webViewer.view.getCanvasSize();
        let canvasSize = Math.min(canvas.x, canvas.y);
        if (canvasSize < 1) canvasSize = 1;

        // Keep points visually connected to avoid see-through artifacts on dense scans.
        const minScreenPixels = 2.2;

        switch (splatMode) {
            case Communicator.PointSizeUnit.CSSPixels:
            case Communicator.PointSizeUnit.ScreenPixels:
                return minScreenPixels;
            case Communicator.PointSizeUnit.World:
                return (minScreenPixels * 5) / canvasSize;
            case Communicator.PointSizeUnit.ProportionOfBoundingDiagonal:
            case Communicator.PointSizeUnit.ProportionOfScreenWidth:
            case Communicator.PointSizeUnit.ProportionOfScreenHeight:
            default:
                return minScreenPixels / canvasSize;
        }
    };
    static setAxis = (
        webViewer: Communicator.IWebViewer,
        isAxis: boolean
    ): void => {
        const axis = webViewer.view.getAxisTriad();
        const isDrawing = webViewer.sheetManager.isDrawingSheetActive();
        if (!isDrawing && webViewer.overlayManager) {
            if (isAxis) axis.enable();
            else axis.disable();
        }
    };
    static setCube = (
        webViewer: Communicator.IWebViewer,
        isCube: boolean
    ): void => {
        const cube = webViewer.view.getNavCube();
        const isDrawing = webViewer.sheetManager.isDrawingSheetActive();
        if (!isDrawing && webViewer.overlayManager) {
            if (isCube) cube.enable();
            else cube.disable();
        }
    };
    static setProjection = (
        webViewer: Communicator.IWebViewer,
        projection: Communicator.Projection
    ): void => {
        // HOOPS can throw during sheet/view transitions if camera updates run when canvas is not sized yet.
        if (!SettingHelper.isViewerCanvasReady(webViewer)) {
            return;
        }
        try {
            webViewer.view.setProjectionMode(projection);
            const currentCamera = webViewer.view.getCamera();
            webViewer.view.setCamera(currentCamera);
        } catch (error) {
            console.warn(
                "[setting-helper] skip setProjection/setCamera due to transient viewer state",
                error
            );
        }
    };
    static toggleLineWeight = (
        webViewer: Communicator.IWebViewer,
        lineWeight: boolean,
        viewId: string
    ): void => {
        GlobalState.mapLineWeight.set(viewId, lineWeight);
        const jsonData = GlobalState.mapJsonSetting.get(viewId);
        if (jsonData)
            JsonSetting.prototype.callApplyLineWeight(
                webViewer,
                jsonData,
                lineWeight
            );
    };
    static setSheetColor = (
        webViewer: Communicator.IWebViewer,
        color: RGBColor
    ): void => {
        const isDrawing = webViewer.sheetManager.isDrawingSheetActive();
        if (isDrawing) {
            const currBG = webViewer.sheetManager.getSheetBackgroundColor();
            const currShadow = webViewer.sheetManager.getSheetShadowColor();
            const newColor = new Communicator.Color(color.r, color.g, color.b);
            webViewer.sheetManager.setSheetColors(currBG, newColor, currShadow);
        }
    };
    static setSheetBG = (
        webViewer: Communicator.IWebViewer,
        color: RGBColor
    ): void => {
        const isDrawing = webViewer.sheetManager.isDrawingSheetActive();
        if (isDrawing) {
            const currColor = webViewer.sheetManager.getSheetColor();
            const currShadow = webViewer.sheetManager.getSheetShadowColor();
            const newBG = new Communicator.Color(color.r, color.g, color.b);
            webViewer.sheetManager.setSheetColors(newBG, currColor, currShadow);
        }
    };
    static setSheetShadow = (
        webViewer: Communicator.IWebViewer,
        color: RGBColor
    ): void => {
        const isDrawing = webViewer.sheetManager.isDrawingSheetActive();
        if (isDrawing) {
            const currColor = webViewer.sheetManager.getSheetColor();
            const currBG = webViewer.sheetManager.getSheetBackgroundColor();
            const newShadow = new Communicator.Color(color.r, color.g, color.b);
            webViewer.sheetManager.setSheetColors(currBG, currColor, newShadow);
        }
    };
    static setAmbient = (
        webViewer: Communicator.IWebViewer,
        ambient: boolean
    ): void => {
        webViewer.view.setAmbientOcclusionEnabled(ambient);
    };
    static setAmbientRad = (
        webViewer: Communicator.IWebViewer,
        ambientRad: number
    ): void => {
        webViewer.view.setAmbientOcclusionRadius(
            ambientRad > 1 ? 1 : ambientRad
        );
    };
    static setAntiAlias = (
        webViewer: Communicator.IWebViewer,
        antiAlias: boolean
    ): void => {
        webViewer.view.setAntiAliasingMode(antiAlias ? 0 : 1);
    };
    static setBloom = (
        webViewer: Communicator.IWebViewer,
        bloom: boolean
    ): void => {
        webViewer.view.setBloomEnabled(bloom);
    };
    static setBloomScale = (
        webViewer: Communicator.IWebViewer,
        bloomScale: number
    ): void => {
        webViewer.view.setBloomIntensityScale(
            bloomScale * Utils.BloomRatioCoefficient
        );
    };
    static setBloomThreshold = (
        webViewer: Communicator.IWebViewer,
        bloomThreshold: number
    ): void => {
        webViewer.view.setBloomThreshold(bloomThreshold / 100.0);
    };
    static setSilhouette = (
        webViewer: Communicator.IWebViewer,
        silhouette: boolean
    ): void => {
        webViewer.view.setSilhouetteEnabled(silhouette);
    };
    static setReflection = (
        webViewer: Communicator.IWebViewer,
        reflection: boolean
    ): void => {
        webViewer.view.setSimpleReflectionEnabled(reflection);
    };
    static setShadows = (
        webViewer: Communicator.IWebViewer,
        reflection: boolean
    ): void => {
        webViewer.view.setSimpleShadowEnabled(reflection);
    };
    static setCappingFaceColor = (
        webViewer: Communicator.IWebViewer,
        color: RGBColor
    ): void => {
        if (!webViewer.cuttingManager.hasActiveCuttingSection()) return;
        const cappingFaceColor = new Communicator.Color(
            color.r,
            color.g,
            color.b
        );
        webViewer.cuttingManager.setCappingFaceColor(cappingFaceColor);
    };
    static setCappingLineColor = (
        webViewer: Communicator.IWebViewer,
        color: RGBColor
    ): void => {
        if (!webViewer.cuttingManager.hasActiveCuttingSection()) return;
        const cappingLineColor = new Communicator.Color(
            color.r,
            color.g,
            color.b
        );
        webViewer.cuttingManager.setCappingLineColor(cappingLineColor);
    };
    static setCappingPlaneColor = (
        webViewer: Communicator.IWebViewer,
        color: RGBColor
    ): void => {
        if (!webViewer.cuttingManager.hasActiveCuttingSection()) return;
        const cappingPlaneColor = new Communicator.Color(
            color.r,
            color.g,
            color.b
        );
        webViewer.cuttingManager.setCuttingPlaneColor(cappingPlaneColor);
    };
    static setSelectionMeasurement = (
        webViewer: Communicator.IWebViewer,
        color: RGBColor
    ): void => {
        const measureColor = new Communicator.Color(color.r, color.g, color.b);
        webViewer.measureManager.setMeasurementColor(measureColor);
    };
    static setPMI = (
        webViewer: Communicator.IWebViewer,
        pmi: boolean
    ): void => {
        webViewer.model && webViewer.model.setPmiColorOverride(pmi);
    };
    static setPMIColor = (
        webViewer: Communicator.IWebViewer,
        color: RGBColor
    ): void => {
        const pmiColor = new Communicator.Color(color.r, color.g, color.b);
        webViewer.model && webViewer.model.setPmiColor(pmiColor);
    };
    static setEyeDome = (
        webViewer: Communicator.IWebViewer,
        eyeDome: number
    ): void => {
        if (eyeDome > 0) {
            webViewer.view.setEyeDomeLightingEnabled(true);
            webViewer.view.setEyeDomeLightingOpacity(eyeDome);
        } else {
            webViewer.view.setEyeDomeLightingEnabled(false);
        }
    };
    static setSplats = (
        webViewer: Communicator.IWebViewer,
        splats: boolean,
        splatsSize: number,
        splatsMode: number
    ): void => {
        const isDrawing = webViewer.sheetManager.isDrawingSheetActive();
        const sizeRaw = SettingHelper.getPointSize(
            splatsSize,
            splatsMode,
            webViewer
        );
        const minSize = SettingHelper.getMinimumPointSize(
            splatsMode,
            webViewer
        );
        const size = Math.max(sizeRaw, minSize);
        if (isDrawing) {
            webViewer.view.setPointSize(
                SystemConstants.POINT_SIZE_DEFAULT,
                Communicator.PointSizeUnit.ScreenPixels
            );
        } else if (splats) {
            webViewer.view.setPointSize(size, splatsMode);
        } else {
            webViewer.view.setPointSize(
                SystemConstants.POINT_SIZE_DEFAULT,
                Communicator.PointSizeUnit.ScreenPixels
            );
        }
    };
    static setZoomSpeed = (
        webViewer: Communicator.IWebViewer,
        zoomSpeed: number
    ): void => {
        const o = webViewer.view.operatorManager.getOperator(
            Communicator.OperatorId.Zoom
        ) as CZoomOperator;
        if (o) {
            o.setZoomSpeedFactor(zoomSpeed);
        }
    };
    static setBimOrbitEnabled = (
        viewId: string | undefined,
        value: boolean
    ): void => {
        if (viewId) {
            const setOperator = GlobalState.getSetOperator(viewId);
            const { currentOperator } = setOperator;
            if (
                currentOperator instanceof OrbitOperator ||
                currentOperator instanceof SelectOperator ||
                currentOperator instanceof AreaSelectOperator
            ) {
                currentOperator.setBimOrbitEnableExt(value);
            }
        }
    };
}
